import React, { useCallback, useState } from "react";
import "../Quiz/Quiz.css";
import "./ChartGame.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { AnswerResult } from "../Quiz/answer.ts";
import { factKey, recordCorrect, recordMiss } from "../Quiz/missedFacts.ts";
import { MissedFactsPractice } from "../Quiz/MissedFactsPractice.tsx";
import { track } from "../../Shared/analytics.ts";
import { getRandomInt } from "../../Shared/Helpers.tsx";
import { ChartGame } from "./ChartGame.tsx";
import { ChartOperation, ChartProgress } from "./chartGame.ts";
import { chartGameEventParams } from "./gameEventParams.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

interface ChartGamePageProps {
    slug: string;
    operation: Operations;
}

const pickIndex = (n: number) => getRandomInt(0, n - 1);

/**
 * The fill-the-chart game (ADR 0054), wrapped in its keyword landing page.
 * A quiz route carrying `game: "chart"`; there is no settings panel, because
 * the chart is the problem set (§4). The route's operation is the chart it
 * opens on - addition on `/addition-chart-game`, otherwise multiplication
 * (ADR 0098 §2) - and the player can switch.
 */
export function ChartGamePage({ slug, operation }: ChartGamePageProps) {
    const entry = findBySlug(slug)!;
    const t = useTranslation();
    const [missesVersion, setMissesVersion] = useState(0);

    const handleAnswer = useCallback((front: string, result: AnswerResult) => {
        if (result === "correct") {
            recordCorrect(factKey(front));
        } else if (result === "wrong") {
            recordMiss(factKey(front));
        }
        setMissesVersion((v) => v + 1);
    }, []);

    // The chart that was filled, not the route's: the player can switch.
    const handleComplete = useCallback((progress: ChartProgress, chart: ChartOperation) => {
        track("quiz_completed", chartGameEventParams(chart, progress));
    }, []);

    return (
        <>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool chartTool">
                <p className="chartIntro">{t("flashCards.chartIntro")}</p>
                <ChartGame pick={pickIndex} operation={operation} onAnswer={handleAnswer} onComplete={handleComplete} />
                <MissedFactsPractice refreshKey={missesVersion} />
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t("flashCards.frequentlyAskedQuestions")}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </>
    );
}

export default ChartGamePage;
